import React from 'react';
import { Check, CheckCheck, Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { format } from 'date-fns';

/**
 * OpsApprovalCheckmark - Single/Double tick for operational approval status
 * Single tick (✓) = Ops approved
 * Double tick (✓✓) = Ops + Customer approved
 */
export const OpsApprovalCheckmark = ({ 
  invoiceId, 
  isOpsApproved, 
  isCustomerApproved, 
  onApprove, 
  loading,
  opsApprovedByName,
  opsApprovedAt
}) => {
  const getCheckmarkStyle = () => {
    if (isCustomerApproved) {
      // Double tick - both ops and customer approved
      return "text-emerald-500";
    } else if (isOpsApproved) {
      // Single green tick - ops approved
      return "text-emerald-500";
    } else {
      // Grey tick - not approved
      return "text-gray-400 hover:text-gray-600";
    }
  };

  const getTooltipText = () => {
    if (isCustomerApproved) {
      return "Fully Approved (Ops + Customer)";
    } else if (isOpsApproved) {
      const approvedInfo = opsApprovedByName ? ` by ${opsApprovedByName}` : '';
      const dateInfo = opsApprovedAt ? ` on ${format(new Date(opsApprovedAt), 'MMM d, yyyy')}` : '';
      return `Operationally Approved${approvedInfo}${dateInfo}. Click to unapprove.`;
    } else {
      return "Click to mark as Operationally Valid";
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onApprove(invoiceId, !isOpsApproved)}
            disabled={loading || isCustomerApproved}
            className={`p-1 rounded transition-colors ${getCheckmarkStyle()} ${loading ? 'opacity-50' : ''} ${isCustomerApproved ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-muted'}`}
            data-testid={`ops-approve-${invoiceId}`}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isCustomerApproved ? (
              <CheckCheck className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" strokeWidth={isOpsApproved ? 3 : 2} />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{getTooltipText()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default OpsApprovalCheckmark;
